import { useEffect, useMemo, useState } from 'react'
import { TaskActualWorkApproveUserDto } from '../../../../../../applications/ports/taskActualWorkApproveRepository'

interface UseFilterStateProps {
  allUsersWithStatus: TaskActualWorkApproveUserDto[]
  onFilteredUsersChange?: (users: TaskActualWorkApproveUserDto[]) => void
}

export const useFilterState = ({
  allUsersWithStatus,
  onFilteredUsersChange,
}: UseFilterStateProps) => {
  const [selectedDivisionUuid, setSelectedDivisionUuid] = useState<
    string | undefined
  >()
  const [selectedTeamUuid, setSelectedTeamUuid] = useState<string | undefined>()
  const [selectedRequestStatus, setSelectedRequestStatus] = useState<
    string | undefined
  >()

  const divisions = useMemo(() => {
    const divisionMap = new Map()
    allUsersWithStatus.forEach(user => {
      const div = user.projectMemberDetail.user.division
      if (div && !divisionMap.has(div.uuid)) {
        divisionMap.set(div.uuid, {
          uuid: div.uuid,
          displayName: div.displayName,
        })
      }
    })
    return Array.from(divisionMap.values())
  }, [allUsersWithStatus])

  const teams = useMemo(() => {
    const teamMap = new Map()
    allUsersWithStatus.forEach(user => {
      const team = user.projectMemberDetail.team
      if (team && !teamMap.has(team.uuid)) {
        teamMap.set(team.uuid, {
          uuid: team.uuid,
          displayName: team.displayName,
        })
      }
    })
    return Array.from(teamMap.values())
  }, [allUsersWithStatus])

  const filteredUsers = useMemo(() => {
    const filterByRequestStatus = (
      users: TaskActualWorkApproveUserDto[]
    ): TaskActualWorkApproveUserDto[] => {
      switch (selectedRequestStatus) {
        case 'REQUESTING':
          return users.filter(u => u.requestingCount > 0)
        case 'UNREQUESTED':
          return users.filter(u => u.unrequestedCount > 0)
        case 'WORKING_HOURS_NOT_ENTERED':
          return users.filter(u => u.missingActualWorkingHourFlag)
        case 'TASK_HOURS_NOT_ENTERED':
          return users.filter(u => u.missingTaskActualWorkFlag)
        default:
          return users
      }
    }

    return filterByRequestStatus(allUsersWithStatus)
      .filter(
        u =>
          !selectedDivisionUuid ||
          u.projectMemberDetail.user.division?.uuid === selectedDivisionUuid
      )
      .filter(
        u =>
          !selectedTeamUuid ||
          u.projectMemberDetail.team?.uuid === selectedTeamUuid
      )
      .sort((a, b) =>
        (a.projectMemberDetail.user.name || '').localeCompare(
          b.projectMemberDetail.user.name || ''
        )
      )
  }, [
    allUsersWithStatus,
    selectedRequestStatus,
    selectedDivisionUuid,
    selectedTeamUuid,
  ])

  // フィルタリング済みユーザーリストを親に通知
  useEffect(() => {
    if (onFilteredUsersChange) {
      onFilteredUsersChange(filteredUsers)
    }
  }, [filteredUsers, onFilteredUsersChange])

  return {
    selectedDivisionUuid,
    selectedTeamUuid,
    selectedRequestStatus,
    setSelectedDivisionUuid,
    setSelectedTeamUuid,
    setSelectedRequestStatus,
    divisions,
    teams,
    filteredUsers,
  }
}
